<script setup>
  import { computed } from 'vue';
  import Icon from '@/components/icons/Icon.vue';
  import { useDateHelper } from '@/composables/useDateHelper';

  const props = defineProps({
    alerts: {
      type: Array,
      default: () => []
    },
    groupBySpot: {
      type: Boolean,
      default: false
    },
    showSpotName: {
      type: Boolean,
      default: true
    }
  });

  const { sentTimeLabel, observationTimeLabel } = useDateHelper();

  const sortedAlerts = computed(() => {
    return [...props.alerts].sort((a, b) => {
      const aTime = new Date(a.sent_time).getTime();
      const bTime = new Date(b.sent_time).getTime();
      return bTime - aTime;
    });
  });

  const sections = computed(() => {
    if (!props.groupBySpot) {
      return [{ key: 'flat', header: null, alerts: sortedAlerts.value }];
    }

    const groups = new Map();
    for (const alert of sortedAlerts.value) {
      const key = alert.spot_id;
      if (!groups.has(key)) {
        groups.set(key, { key, header: alert.spot_name, alerts: [] });
      }
      groups.get(key).alerts.push(alert);
    }

    return Array.from(groups.values()).sort((a, b) => {
      const aLatest = new Date(a.alerts[0].sent_time).getTime();
      const bLatest = new Date(b.alerts[0].sent_time).getTime();
      return bLatest - aLatest;
    });
  });

  const showSpotNameInRow = computed(() => props.showSpotName && !props.groupBySpot);

  function spotHref(alert) {
    if (alert?.spot_id == null) return '#';
    return `../spot/${alert.spot_id}`;
  }

  function alertIcon(alert) {
    return alert.is_obs ? 'markers' : 'binoculars';
  }

  function rowKey(alert) {
    return alert.id || `${alert.alert_id}-${alert.sent_time}`;
  }
</script>

<template>
  <div v-for="section in sections" :key="section.key" class="jwx-modal__section">
    <div v-if="section.header" class="jwx-modal__section-header">
      <h3 class="jwx-modal__section-title">{{ section.header }}</h3>
    </div>
    <div class="jwx-modal__section-body">
      <ul class="jwx-block jwx-table-view" data-surface="1">
        <li
          v-for="alert in section.alerts"
          :key="rowKey(alert)"
          class="jwx-table-view__row"
        >
          <a :href="spotHref(alert)" class="jwx-table-view__action-row jwx-p-block-md">
            <span class="jwx-alert-history-list__icon-circle">
              <Icon :name="alertIcon(alert)" />
            </span>
            <div class="jwx-table-view__row-main-content">
              <p v-if="showSpotNameInRow" class="jwx-table-view__row-label jwx-font-weight-700">{{ alert.spot_name }}</p>
              <p class="jwx-table-view__row-text jwx-font-size-sm jwx-text-color jwx-font-weight-400">{{ alert.alert_text }}</p>
              <p class="jwx-table-view__row-text">{{ observationTimeLabel(alert.observation_time) }}</p>
            </div>
            <div class="jwx-table-view__row-actions">
              <span class="jwx-table-view__row-value">{{ sentTimeLabel(alert.sent_time) }}</span>
              <div class="jwx-table-view__action-row-detail">
                <Icon name="chevron-right" />
              </div>
            </div>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .jwx-alert-history-list__icon-circle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    border-radius: 50%;
    background-color: var(--jwx-surface);
  }
</style>
